
import { forwardRef } from "react"
import { ArrowRightIcon, QuoteIcon } from "lucide-react"

import { JobWidget } from "../Offerspage/JobWidgets"
import { cn } from "../../lib/utils"

/**
 * Live stage beside the hero chooser (concept 1 · H-15 × H-11): the chosen job shown as WhatsApp pieces
 * from two industries (Mailmodo widget collage), a one-line "how it works" node strip (Mailchimp flow
 * nodes) and the matched customer metric as a proof chip. Sits in the same viewport as the tiles on desktop.
 */
export const HeroStage = forwardRef(function HeroStage(
  { label, caption, cards, steps, quote, stateKey, className },
  ref
) {
  return (
    <div
      ref={ref}
      aria-live="polite"
      className={cn(
        "bg-stage-dots flex scroll-mt-32 flex-col gap-4 rounded-2xl border border-border bg-background p-4 shadow-card-float sm:p-5",
        className
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] font-bold tracking-[0.08em] text-muted-foreground uppercase">
          {label}
        </span>

        {caption && (
          <span className="rounded-md bg-charcoal px-2 py-0.5 text-xs font-semibold text-white">
            {caption}
          </span>
        )}
      </div>

      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
        {cards.map((c, i) => (
          <li
            key={`${stateKey}-${c.industry}`}
            className={cn(
              "animate-node-in flex flex-col gap-2.5 rounded-xl bg-surface-soft p-3.5 lg:p-5",
              i > 0 && "lg:hidden"
            )}
            style={{ animationDelay: `${i * 80}ms` }}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="rounded-md border border-border bg-background px-2 py-0.5 text-[11px] font-semibold">
                {c.industry}
              </span>

              {c.tag && (
                <span className="text-[11px] font-semibold text-brand-text">
                  {c.tag}
                </span>
              )}
            </div>

            {c.widgets.map((w, k) => (
              <JobWidget
                key={k}
                w={w}
                className={w.kind === "out" ? "self-end" : undefined}
              />
            ))}
          </li>
        ))}
      </ul>

      <ol className="flex flex-wrap items-center gap-1.5" aria-label="How it works">
        {steps.map((s, i) => (
          <li key={`${stateKey}-${s}`} className="flex items-center gap-1.5">
            <span className="rounded-md bg-brand-tint px-2 py-1 text-xs font-semibold text-brand-text">
              {s}
            </span>

            {i < steps.length - 1 && (
              <ArrowRightIcon
                className="size-3.5 text-brand"
                aria-hidden="true"
              />
            )}
          </li>
        ))}
      </ol>

      <a
        href={quote.href}
        onClick={quote.onClick}
        className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs hover:border-ink-strong"
      >
        <QuoteIcon
          className="size-3.5 shrink-0 text-brand"
          aria-hidden="true"
        />

        <span>
          <span className="font-semibold text-brand-text">
            {quote.metric}
          </span>{" "}
          {quote.label} · {quote.by}
        </span>
      </a>
    </div>
  )
})
